import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import ReactApexChart from 'react-apexcharts';

import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';

import { apiService } from '../../../../services/ApiService';

import Card from '../../../../components/Card/Card';
import CardBody from '../../../../components/Card/CardBody';
import RangeSelect from './RangeSelect';
import { createAreaChartOptions } from './chartOptions';
import { FilterIcon } from '../../../../components/Icons/Icons';

const fuelTypeOptions = {
  DSL: 'Diesel',
  E10: 'Ethanol 10',
  E85: 'Ethanol 85',
  LNG: 'Liquefied Natural Gas',
  LPG: 'Liquefied Petroleum Gas',
  PDSL: 'Premium Diesel',
  P95: 'Premium Unleaded 95',
  P98: 'Premium Unleaded 98',
  U91: 'Unleaded 91',
};

function FuelPricesChart() {
  const [fuelType, setFuelType] = useState('DSL');
  const [period, setPeriod] = useState('year');

  const { data: chart = [], isLoading } = useQuery({
    queryKey: ['fuel-prices-chart', period, fuelType],
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () => {
      const response = await apiService.getStatsFuelPricesChart({
        range: period,
        type: fuelType,
      });

      return response.data;
    },
  });

  const handleFuelType = (type) => {
    setFuelType(type);
  };

  const series = useMemo(
    () => [
      {
        name: 'Average Price',
        data: chart.map((item) => item.avgPrice),
      },
      {
        name: 'Maximum Price',
        data: chart.map((item) => item.maxPrice),
      },
      {
        name: 'Minimum Price',
        data: chart.map((item) => item.minPrice),
      },
    ],
    [chart],
  );

  const options = useMemo(
    () =>
      createAreaChartOptions({
        colors: ['#8C99ED', '#1870EC', '#F28C28'],
        categories: chart.map((item) => new Date(item.date).toISOString()),
        yFormatter: (value) => value.toFixed(0),
        tooltipFormatter: (value) => value.toFixed(2),
        legend: true,
      }),
    [chart],
  );

  return (
    <Card minH={301} padding="24px 16px 12px 16px" w="100%">
      <Flex justifyContent="center" mb="12px">
        <Text variant="title-lg">Fuel Prices</Text>
      </Flex>

      <RangeSelect period={period} setPeriod={setPeriod} />

      <Flex align="center" mb="12px">
        <Menu variant="filter">
          <MenuButton
            as={Button}
            leftIcon={<FilterIcon color="white" fontSize="24px" />}
            variant="secondary-md"
            color="white"
            w="300px"
            px="24px !important"
          >
            {fuelTypeOptions[fuelType]}
          </MenuButton>

          <MenuList className="context-menu">
            {Object.entries(fuelTypeOptions).map(([value, label]) => (
              <MenuItem key={value} onClick={() => handleFuelType(value)}>
                {label}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </Flex>

      <CardBody w="100%" padding="0">
        <Box w="100%">
          {isLoading ? (
            <Flex justifyContent="center" alignItems="center" height="315px">
              <Text>Loading chart data...</Text>
            </Flex>
          ) : (
            <ReactApexChart
              options={options}
              series={series}
              type="area"
              height={300}
              className="full-width-members-dashboard-chart"
            />
          )}
        </Box>
      </CardBody>
    </Card>
  );
}

export default FuelPricesChart;
